/**
 * Rainbow AI Radar dBZ to Gradient Mapper
 *
 * Converts the complete Rainbow AI 128-color table (dBZ -32 to 95) into
 * continuous CSS linear-gradient strings for rain and snow precipitation types.
 *
 * This provides smooth color interpolation similar to Windy's colormap style,
 * using the full fidelity of the Rainbow AI color palette.
 */
import { t } from "i18next";

export interface IGradientColorMapItem {
  dbz: number;
  color: string;
  position: number;
}

/**
 * Complete Rainbow AI Rain Color Table (dBZ -32 to 95)
 * 128 color stops with 8-digit hex (#RRGGBBAA) including alpha channel
 */
const RAIN_DBZ_COLORS: Array<{ dbz: number; color: string }> = [
  { dbz: 0, color: "#005eb600" },
  { dbz: 1, color: "#005eb607" },
  { dbz: 2, color: "#005eb60f" },
  { dbz: 3, color: "#005eb616" },
  { dbz: 4, color: "#005eb61e" },
  { dbz: 5, color: "#005eb626" },
  { dbz: 6, color: "#005eb62d" },
  { dbz: 7, color: "#005eb635" },
  { dbz: 8, color: "#005eb63d" },
  { dbz: 9, color: "#005eb644" },
  { dbz: 10, color: "#005eb64c" },
  { dbz: 11, color: "#005eb654" },
  { dbz: 12, color: "#005eb65b" },
  { dbz: 13, color: "#005eb663" },
  { dbz: 14, color: "#005eb66b" },
  { dbz: 15, color: "#005eb672" },
  { dbz: 16, color: "#005eb67a" },
  { dbz: 17, color: "#005eb682" },
  { dbz: 18, color: "#005eb689" },
  { dbz: 19, color: "#005eb691" },
  { dbz: 20, color: "#005eb699" },
  { dbz: 21, color: "#075cb4a6" },
  { dbz: 22, color: "#0e5bb3b4" },
  { dbz: 23, color: "#155ab1c1" },
  { dbz: 24, color: "#1c59b0cf" },
  { dbz: 25, color: "#2458afdd" },
  { dbz: 26, color: "#3955abe3" },
  { dbz: 27, color: "#4e52a7ea" },
  { dbz: 28, color: "#6350a3f1" },
  { dbz: 29, color: "#784d9ff8" },
  { dbz: 30, color: "#8e4b9bff" },
  { dbz: 31, color: "#a44c92ff" },
  { dbz: 32, color: "#ba4e89ff" },
  { dbz: 33, color: "#d04f81ff" },
  { dbz: 34, color: "#e65178ff" },
  { dbz: 35, color: "#fc5370ff" },
  { dbz: 36, color: "#fc676fff" },
  { dbz: 37, color: "#fd7b6fff" },
  { dbz: 38, color: "#fd8f6eff" },
  { dbz: 39, color: "#fea36eff" },
  { dbz: 40, color: "#ffb76eff" },
  { dbz: 41, color: "#ffc559ff" },
  { dbz: 42, color: "#ffd344ff" },
  { dbz: 43, color: "#ffe12fff" },
  { dbz: 44, color: "#ffef19ff" },
  { dbz: 45, color: "#fffd05ff" },
  { dbz: 46, color: "#fffd05ff" },
  { dbz: 47, color: "#fffd05ff" },
  { dbz: 48, color: "#fffd05ff" },
  { dbz: 49, color: "#fffd05ff" },
  { dbz: 50, color: "#fffd05ff" },
  // { dbz: 51, color: "#fffd05ff" },
  // { dbz: 52, color: "#fffd05ff" },
  // { dbz: 53, color: "#fffd05ff" },
  // { dbz: 54, color: "#fffd05ff" },
  // { dbz: 55, color: "#fffd05ff" },
  // { dbz: 56, color: "#fffd05ff" },
  // { dbz: 57, color: "#fffd05ff" },
  // { dbz: 58, color: "#fffd05ff" },
  // { dbz: 59, color: "#fffd05ff" },
  // { dbz: 60, color: "#fffd05ff" },
];

/**
 * Complete Rainbow AI Snow Color Table (dBZ -32 to 95)
 * 128 color stops with 8-digit hex (#RRGGBBAA) including alpha channel
 */
const SNOW_DBZ_COLORS: Array<{ dbz: number; color: string }> = [
  { dbz: 0, color: "#cfffff00" },
  { dbz: 1, color: "#ceffff0c" },
  { dbz: 2, color: "#cdffff19" },
  { dbz: 3, color: "#ccffff26" },
  { dbz: 4, color: "#cbffff33" },
  { dbz: 5, color: "#cbffff3f" },
  { dbz: 6, color: "#caffff4c" },
  { dbz: 7, color: "#c9ffff59" },
  { dbz: 8, color: "#c8ffff66" },
  { dbz: 9, color: "#c7ffff72" },
  { dbz: 10, color: "#c7ffff7f" },
  { dbz: 11, color: "#c6ffff8c" },
  { dbz: 12, color: "#c5ffff99" },
  { dbz: 13, color: "#c4ffffa5" },
  { dbz: 14, color: "#c3ffffb2" },
  { dbz: 15, color: "#c3ffffbf" },
  { dbz: 16, color: "#c2ffffcc" },
  { dbz: 17, color: "#c1ffffd8" },
  { dbz: 18, color: "#c0ffffe5" },
  { dbz: 19, color: "#bffffff2" },
  { dbz: 20, color: "#bfffffff" },
  { dbz: 21, color: "#b8f8ffff" },
  { dbz: 22, color: "#b2f2ffff" },
  { dbz: 23, color: "#abebffff" },
  { dbz: 24, color: "#a5e5ffff" },
  { dbz: 25, color: "#9fdfffff" },
  { dbz: 26, color: "#98d8ffff" },
  { dbz: 27, color: "#92d2ffff" },
  { dbz: 28, color: "#8bcbffff" },
  { dbz: 29, color: "#85c5ffff" },
  { dbz: 30, color: "#7fbfffff" },
  { dbz: 31, color: "#78b8ffff" },
  { dbz: 32, color: "#72b2ffff" },
  { dbz: 33, color: "#6babffff" },
  { dbz: 34, color: "#65a5ffff" },
  { dbz: 35, color: "#5f9fffff" },
  { dbz: 36, color: "#5b9bffff" },
  { dbz: 37, color: "#5898ffff" },
  { dbz: 38, color: "#5595ffff" },
  { dbz: 39, color: "#5292ffff" },
  { dbz: 40, color: "#4f8fffff" },
  { dbz: 41, color: "#4b8bffff" },
  { dbz: 42, color: "#4888ffff" },
  { dbz: 43, color: "#4585ffff" },
  { dbz: 44, color: "#4282ffff" },
  { dbz: 45, color: "#3f7fffff" },
  { dbz: 46, color: "#3b7bffff" },
  { dbz: 47, color: "#3878ffff" },
  { dbz: 48, color: "#3575ffff" },
  { dbz: 49, color: "#3272ffff" },
  { dbz: 50, color: "#2f6fffff" },
  // { dbz: 51, color: "#2b6bffff" },
  // { dbz: 52, color: "#2868ffff" },
  // { dbz: 53, color: "#2565ffff" },
  // { dbz: 54, color: "#2262ffff" },
  // { dbz: 55, color: "#1f5fffff" },
  // { dbz: 56, color: "#1b5bffff" },
  // { dbz: 57, color: "#1858ffff" },
  // { dbz: 58, color: "#1555ffff" },
  // { dbz: 59, color: "#1252ffff" },
  // { dbz: 60, color: "#0f4fffff" },
];

/**
 * Builds a CSS linear-gradient string from an array of color stops
 *
 * Calculates percentage positions for each dBZ value within the range
 * and creates a continuous gradient string compatible with CSS background property.
 *
 * @param colors - Array of dBZ/color pairs
 * @returns CSS linear-gradient string (e.g., "linear-gradient(to right, #000 0%, #fff 100%)")
 */
export const buildCSSGradient = (
  colors: Array<{ dbz: number; color: string }>
): string => {
  if (colors.length === 0) return "";

  const minDbz = colors[0].dbz;
  const maxDbz = colors[colors.length - 1].dbz;
  const dbzRange = maxDbz - minDbz;

  const colorStops = colors.map((item) => {
    const position = ((item.dbz - minDbz) / dbzRange) * 100;
    return `${item.color} ${position.toFixed(2)}%`;
  });

  return `linear-gradient(to right, ${colorStops.join(", ")})`;
};

/**
 * Returns the full rain gradient color array with position calculations
 *
 * @returns Array of rain color stops with dBZ, color, and position
 */
export const getRainGradientColors = (): IGradientColorMapItem[] => {
  const minDbz = RAIN_DBZ_COLORS[0].dbz;
  const maxDbz = RAIN_DBZ_COLORS[RAIN_DBZ_COLORS.length - 1].dbz;
  const dbzRange = maxDbz - minDbz;

  return RAIN_DBZ_COLORS.map((item) => ({
    dbz: item.dbz,
    color: item.color,
    position: ((item.dbz - minDbz) / dbzRange) * 100,
  }));
};

/**
 * Returns the full snow gradient color array with position calculations
 *
 * @returns Array of snow color stops with dBZ, color, and position
 */
export const getSnowGradientColors = (): IGradientColorMapItem[] => {
  const minDbz = SNOW_DBZ_COLORS[0].dbz;
  const maxDbz = SNOW_DBZ_COLORS[SNOW_DBZ_COLORS.length - 1].dbz;
  const dbzRange = maxDbz - minDbz;

  return SNOW_DBZ_COLORS.map((item) => ({
    dbz: item.dbz,
    color: item.color,
    position: ((item.dbz - minDbz) / dbzRange) * 100,
  }));
};

/**
 * LEGACY FUNCTIONS - Kept for backward compatibility
 * These are used by the old getDualRadarColormap() function
 */

export type IntensityCategory = "light" | "moderate" | "heavy";

export interface ICategoryColorMapItem {
  category: IntensityCategory;
  label: string;
  color: string;
  dbzRange: [number, number];
}

// dBZ range constants for categories
const DBZ_LIGHT_MIN = 0;
const DBZ_LIGHT_MAX = 20;
const DBZ_MODERATE_MIN = 20;
const DBZ_MODERATE_MAX = 40;
const DBZ_HEAVY_MIN = 40;
const DBZ_HEAVY_MAX = 50;

// Simplified colors for category-based display
const RAIN_CATEGORY_COLORS = {
  light: "#00FF00",
  moderate: "#FFFF00",
  heavy: "#FF0000",
};

const SNOW_CATEGORY_COLORS = {
  light: "#00FFFF",
  moderate: "#0080FF",
  heavy: "#0000FF",
};

/**
 * Returns the rain category colormap with 3 categories
 * @deprecated Use getRainGradientColors() for continuous gradients
 */
export const getRainCategoryColormap = (): ICategoryColorMapItem[] => {
  return [
    {
      category: "light",
      label: t("radarLegend.light"),
      color: RAIN_CATEGORY_COLORS.light,
      dbzRange: [DBZ_LIGHT_MIN, DBZ_LIGHT_MAX],
    },
    {
      category: "moderate",
      label: t("radarLegend.moderate"),
      color: RAIN_CATEGORY_COLORS.moderate,
      dbzRange: [DBZ_MODERATE_MIN, DBZ_MODERATE_MAX],
    },
    {
      category: "heavy",
      label: t("radarLegend.heavy"),
      color: RAIN_CATEGORY_COLORS.heavy,
      dbzRange: [DBZ_HEAVY_MIN, DBZ_HEAVY_MAX],
    },
  ];
};

/**
 * Returns the snow category colormap with 3 categories
 * @deprecated Use getSnowGradientColors() for continuous gradients
 */
export const getSnowCategoryColormap = (): ICategoryColorMapItem[] => {
  return [
    {
      category: "light",
      label: t("radarLegend.light"),
      color: SNOW_CATEGORY_COLORS.light,
      dbzRange: [DBZ_LIGHT_MIN, DBZ_LIGHT_MAX],
    },
    {
      category: "moderate",
      label: t("radarLegend.moderate"),
      color: SNOW_CATEGORY_COLORS.moderate,
      dbzRange: [DBZ_MODERATE_MIN, DBZ_MODERATE_MAX],
    },
    {
      category: "heavy",
      label: t("radarLegend.heavy"),
      color: SNOW_CATEGORY_COLORS.heavy,
      dbzRange: [DBZ_HEAVY_MIN, DBZ_HEAVY_MAX],
    },
  ];
};
